import { Box, Typography, Button, IconButton, useTheme } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Premium Map Popup Content Component
 * Reusable component for displaying marker information in Google Maps InfoWindow
 * Supports dark mode and has consistent premium styling
 */
export default function MapPopupContent(props) {
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";

  // Brand colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";

  // Theme-aware colors
  const bgPrimary = isDark ? "#032d42" : "#ffffff";
  const bgSecondary = isDark ? "#021e2d" : "#f8fafc";
  const textPrimary = isDark ? "#ffffff" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(0, 0, 0, 0.06)";
  const iconBg = isDark ? "rgba(160, 196, 109, 0.15)" : "rgba(160, 196, 109, 0.1)";

  const {
    // Header
    title = "",
    subtitle = "",
    
    // Content fields
    lastEvent = null,
    tag = null,
    description = null,
    note = null,
    facility = null,
    date = null,
    coordinates = null,
    
    // Actions
    buttonLabel = "See Data",
    onButtonClick = null,
    showButton = true,
    onClose = null,
  } = props;

  // Premium single-line row (label · value)
  const InfoRow = ({ label, value }) => {
    if (value == null || value === "") return null;
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "4px 0",
          minHeight: 0,
        }}
      >
        <Typography
          component="span"
          sx={{
            fontSize: "0.6875rem",
            color: textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {label}
        </Typography>
        <Typography component="span" sx={{ fontSize: "0.6rem", color: textSecondary, opacity: 0.8, flexShrink: 0 }}>·</Typography>
        <Typography
          component="span"
          sx={{
            fontSize: "0.8125rem",
            color: textPrimary,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      className="map-popup-premium map-popup-compact"
      sx={{
        marginTop: 0,
        paddingTop: 0,
        background: isDark
          ? "linear-gradient(180deg, #043a52 0%, #032d42 50%, #021e2d 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #fafbfd 50%, #f1f5f9 100%)",
        borderRadius: "14px",
        overflow: "visible",
        minWidth: "220px",
        maxWidth: "280px",
        maxHeight: "none",
        display: "flex",
        flexDirection: "column",
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.2)"
          : "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      {/* Premium header – slim blue bar */}
      <Box
        sx={{
          background: `linear-gradient(145deg, ${navyColor} 0%, #0d3d56 50%, #1a4a6a 100%)`,
          padding: "2px 8px 3px",
          position: "relative",
          flexShrink: 0,
          borderTopLeftRadius: "14px",
          borderTopRightRadius: "14px",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: `linear-gradient(90deg, ${greenColor} 0%, rgba(160,196,109,0.5) 70%, transparent 100%)`,
            opacity: 0.95,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            minHeight: 20,
          }}
        >
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "4px",
              background: "rgba(255,255,255,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <EventIcon sx={{ color: "#fff", fontSize: "0.5rem" }} />
          </Box>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "0.7rem",
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
              textShadow: "0 1px 2px rgba(0,0,0,0.2)",
            }}
          >
            {title || "Last Event"}
          </Typography>
          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{
                flexShrink: 0,
                marginLeft: "auto",
                padding: "2px",
                borderRadius: "4px",
                color: "rgba(255,255,255,0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.18)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: "0.7rem" }} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Premium content */}
      <Box
        sx={{
          padding: "10px 12px 12px",
          overflow: "hidden",
          flex: "1 1 auto",
          minHeight: 0,
          borderTop: isDark ? "1px solid rgba(148,163,184,0.08)" : "1px solid rgba(0,41,67,0.06)",
        }}
      >
        <InfoRow label="Tag" value={tag} />
        <InfoRow label="Event" value={lastEvent} />
        <InfoRow label="Description" value={description} />
        <InfoRow label="Note" value={note} />
        <InfoRow label="Facility" value={facility} />
        <InfoRow label="Date" value={date} />
        <InfoRow label="Coordinates" value={coordinates} />
      </Box>

      {/* Premium action */}
      {showButton && onButtonClick && (
        <Box sx={{ padding: "0 12px 12px", flexShrink: 0 }}>
          <Button
            onClick={onButtonClick}
            variant="contained"
            fullWidth
            size="small"
            startIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem" }} />}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "8px 14px",
              backgroundColor: greenColor,
              color: "#ffffff",
              boxShadow: "0 4px 14px rgba(160,196,109,0.35)",
              "&:hover": {
                backgroundColor: "#8ab45a",
                boxShadow: "0 6px 20px rgba(160,196,109,0.4)",
              },
            }}
          >
            {buttonLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
